import axios from 'axios';
import { useAuthStore } from '../store/auth';
import { clearStoredEmbedSession } from './embedSession';
import { usePlanGateStore } from '../store/planGate';
import { markGateHandled, parsePlanGate, parseVerificationGate } from './planGates';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** The page shows its own inline gate for plan/verification 403s; skip the global dialog. */
    skipGateDialog?: boolean;
  }
}

export const apiBaseUrl = import.meta.env.VITE_API_URL ?? '/api/v1';

export const api = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

// When running in embedded mode, authenticate via Bearer token instead of cookies
api.interceptors.request.use((config) => {
  const { embedBearerToken } = useAuthStore.getState();
  if (embedBearerToken) {
    config.headers['Authorization'] = `Bearer ${embedBearerToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url: string = error.config?.url ?? '';
    // Don't interfere with auth endpoints — failed login/register/me is expected
    const isAuthEndpoint = url.startsWith('/auth/');
    // SDK project-key checks can fail independently of the user's dashboard session.
    const isSdkProjectKeyEndpoint = url.startsWith('/sdk/');
    if (error.response?.status === 403) openGateDialog(error);
    if (error.response?.status === 401 && !isAuthEndpoint && !isSdkProjectKeyEndpoint) {
      // Clear auth state so ProtectedRoute redirects via React Router (no page reload)
      const store = useAuthStore.getState();
      store.setUser(null);
      store.setLoading(false);
      if (store.isEmbedded) {
        clearStoredEmbedSession();
        window.dispatchEvent(new CustomEvent('responsedock:session-expired', {
          detail: 'The embedded bearer session expired or was revoked.',
        }));
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Plan and verification 403s open one app-wide dialog (upgrade, or confirm-your-email) instead of
 * failing silently or with raw API text. Embedded sessions belong to a host product's users, so they
 * never see ResponseDock pricing or account prompts.
 */
function openGateDialog(error: { config?: { skipGateDialog?: boolean } }) {
  if (useAuthStore.getState().isEmbedded) return;
  const planGate = parsePlanGate(error);
  const verificationGate = planGate ? null : parseVerificationGate(error);
  if (!planGate && !verificationGate) return;
  if (error.config?.skipGateDialog) return;
  const store = usePlanGateStore.getState();
  if (planGate) store.openUpgrade(planGate);
  else if (verificationGate) store.openVerify(verificationGate);
  markGateHandled(error);
}
